import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../../identity/types';
import { peopleGateway } from '../index';
import { useEmployeeInvitationStates } from '../useEmployeeInvitationStates';
import { EMPLOYEE_PROFILE_STEPS, employmentStatusLabels, type EmployeeProfileStepKey, type EmployeeResponse } from '../types';
import { EmployeeSetupStep } from './steps/EmployeeSetupStep';
import { TaxesExemptionsStep } from './steps/TaxesExemptionsStep';
import { DocumentsStep } from './steps/DocumentsStep';
import { AdditionalSettingsStep } from './steps/AdditionalSettingsStep';
import type { EmployeeProfileStepProps } from './steps/EmployeeSetupStep';
import { SetupSummaryStep } from './steps/SetupSummaryStep';
import { ProfileFooterActions } from '../components/ProfileFooterActions';
import { ProfileStepper } from '../components/ProfileStepper';
import { ReadinessStatusChip, useEmployeeReadiness, type ReadinessChipValue, type ReadinessLoad } from '../../employee-readiness';
import '../employee-profile.css';
import '../employee-profile-layout.css';

// Employee Profile Setup — the shell an existing employee is selected/edited through:
// Employee List -> Edit -> Employee Profile (Step 1: Employee Setup, Step 2: Taxes & Exemptions,
// Step 3: Upload Documents, Step 4: Additional Settings). Route shape and step-in-URL convention mirror
// CustomPayRunWizard's `/payroll-processing/:runId/:step` exactly — an authenticated user resuming a
// specific, already-owned record, not a not-yet-durable wizard (contrast with Admin Sign Up's own
// local-state step, which that page's own comment explains is the opposite case). This page owns the
// selected employee; every child step below receives it (or just its id) as a prop rather than loading or
// selecting its own — see DocumentsStep/AdditionalSettingsStep.
//
// This is the EXISTING-EMPLOYEE / EDIT shell only. Add Employee remains the flat inline form on the
// Employee List page (a later EPN-38 slice covers a real Add/Create wizard) — Edit is not overloaded with
// Create behavior here.
// 'setup-summary' (EPN-94) is rendered separately below — it takes the shared readiness read, not an employee.
const STEP_COMPONENTS: Record<Exclude<EmployeeProfileStepKey, 'setup-summary'>, (props: EmployeeProfileStepProps) => ReactElement> = {
  'employee-setup': EmployeeSetupStep,
  'taxes-exemptions': TaxesExemptionsStep,
  documents: DocumentsStep,
  'additional-settings': AdditionalSettingsStep,
};

export function EmployeeProfilePage() {
  const { employeeId, step } = useParams<{ employeeId: string; step: string }>();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<EmployeeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Screen 01: the heading follows First/Last Name as they are typed, before anything is saved. It is only a preview of what is in the inputs; the
  // employee record (and so everything else on this page) changes only after a real save. Cleared whenever a different or freshly saved employee arrives.
  const [typedName, setTypedName] = useState<{ first: string; last: string; step: EmployeeProfileStepKey } | null>(null);
  // EPN-94: readiness is computed by the server and read once per employee here, so the header chip and the
  // Setup Summary step always show the same result. It never gates the profile itself — if it fails, the
  // profile still loads and only the readiness UI shows its own error/forbidden state.
  const { load: readinessLoad, reload: reloadReadiness } = useEmployeeReadiness(employeeId);
  // While the employee's invitation is pending, or accepted but not yet submitted, they complete their own profile; an administrator editing it meanwhile would collide with them.
  // The list disables Edit; this covers a typed or bookmarked address. The read-only Setup Summary stays reachable.
  const invitationIds = useMemo(() => (employeeId ? [employeeId] : []), [employeeId]);
  const invitationStates = useEmployeeInvitationStates(invitationIds);
  const invitationState = employeeId === undefined ? undefined : invitationStates[employeeId];
  // An accepted invitation keeps the profile with the employee until they submit it (the list shows "Onboarding in Progress").
  const employeeOwnsProfile = invitationState === 'pending'
    || (invitationState === 'accepted' && employee !== null && !employee.onboardingSubmittedAtUtc && employee.status === 0);
  useEffect(() => {
    if (employeeOwnsProfile && step !== 'setup-summary') navigate('/people/employees', { replace: true });
  }, [employeeOwnsProfile, step, navigate]);

  const loadEmployee = useCallback(async () => {
    if (!employeeId) return;
    setLoading(true);
    setError(null);
    setNotFound(false);
    setTypedName(null);
    try {
      // Always reloaded from the backend by id — never trusted from React Router navigation state, a
      // previously loaded Employee List, or any client-side cache. A direct reload/shared link must load
      // the same way a click-through from the list does. The backend's own company scoping
      // (EmployeeService.GetByIdAsync -> FindInOwnCompanyAsync) is what actually rejects a manipulated
      // cross-company employeeId (a 404, indistinguishable from "doesn't exist") — this page's own
      // not-found handling below is a safe presentation of that rejection, not a second access control.
      setEmployee(await peopleGateway.getEmployee(employeeId));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setNotFound(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load this employee.');
      }
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadEmployee();
  }, [loadEmployee]);

  // Neutral, current-step-only highlighting — no step is ever marked "done" merely because it was
  // visited. Real completion state doesn't exist yet (it depends on persisted setup state this shell
  // slice doesn't implement), so this deliberately shows nothing that would misrepresent progress.
  const activeStep: EmployeeProfileStepKey = EMPLOYEE_PROFILE_STEPS.some((s) => s.key === step)
    ? (step as EmployeeProfileStepKey)
    : 'employee-setup';
  const goToStep = useCallback(
    (next: EmployeeProfileStepKey) => navigate(`/people/${employeeId}/${next}`),
    [navigate, employeeId],
  );
  const activeStepIndex = EMPLOYEE_PROFILE_STEPS.findIndex((s) => s.key === activeStep);

  function handleClearDraft() {
    // No draft state is held anywhere outside each step's own local component state (no localStorage, no
    // client-side cache of unsaved edits) — there is nothing to actually clear yet. This confirms the
    // action the way the approved design does without claiming to erase something that doesn't exist.
    window.confirm('Clear all unsaved changes on this step? This cannot be undone.');
  }

  function handleGoBack() {
    const previous = EMPLOYEE_PROFILE_STEPS[activeStepIndex - 1];
    if (previous) {
      goToStep(previous.key);
    } else {
      navigate('/people');
    }
  }

  function handleSaveAndExit() {
    // Pure navigation — see ProfileFooterActions' own comment on why this never claims a save happened.
    navigate('/people');
  }

  function handleSaveAndContinue() {
    const next = EMPLOYEE_PROFILE_STEPS[activeStepIndex + 1];
    if (next) {
      goToStep(next.key);
    } else {
      navigate('/people');
    }
  }

  if (loading) {
    return (
      <section className="card card--wide">
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (notFound) {
    return (
      <section className="card card--wide">
        <p className="error">Employee not found.</p>
        <button type="button" className="link" onClick={() => navigate('/people')}>Back to People</button>
      </section>
    );
  }

  if (error || !employee) {
    return (
      <section className="card card--wide">
        <p className="error">{error ?? 'Could not load this employee.'}</p>
        <button type="button" className="link" onClick={() => navigate('/people')}>Back to People</button>
      </section>
    );
  }

  const isSummary = activeStep === 'setup-summary';
  const savedName = `${employee.firstName} ${employee.lastName}`.replace(/\s+/g, ' ').trim();
  // The typed text lives in Step 1's inputs, so it only counts while that step is on screen; leaving the step discards it, and so does the heading.
  const liveName = typedName && typedName.step === activeStep ? typedName : null;
  const headingName = (liveName ? `${liveName.first} ${liveName.last}`.replace(/\s+/g, ' ').trim() : savedName) || 'New Employee';

  // Steps that have a section list draw the footer inside their bordered panel (Figma); the others get it below their content.
  const footer = (
    <ProfileFooterActions
      onClearDraft={handleClearDraft}
      onGoBack={handleGoBack}
      onSaveAndExit={handleSaveAndExit}
      onSaveAndContinue={handleSaveAndContinue}
    />
  );
  const footerInsidePanel = activeStep === 'employee-setup' || activeStep === 'taxes-exemptions' || activeStep === 'documents';
  const statusPill = (
    <span className={`epx-status-pill epx-status-pill--${statusModifier(employee.status)}`}>
      <span className="epx-status-pill__dot" aria-hidden="true" />
      {employmentStatusLabels[employee.status]}
    </span>
  );

  return (
    <div className="epx-page">
      <div className="epx-titlebar">
        <h1 className="epx-title">Employee Profile Setup</h1>
        <button type="button" className="link" onClick={() => navigate('/people')}>
          Back to People
        </button>
      </div>

      <section className="card card--wide employee-profile epx">
        <div className="epx-head">
          <div className="epx-identity employee-profile-header">
            <h2 data-testid="profile-heading">{headingName}</h2>
            {liveName && headingName !== savedName && <p className="muted" role="status">Name not saved yet.</p>}
            <p className="muted epx-meta">
              {employee.employeeNumber}{' '}
              {employee.isNewHire && <><span className="employee-profile-new-hire">New hire</span>{' '}</>}
              <button
                type="button"
                className="readiness-chip-button"
                onClick={() => goToStep('setup-summary')}
                aria-label={`${chipAriaLabel(readinessLoad)} View setup summary.`}
              >
                <ReadinessStatusChip value={chipValue(readinessLoad)} />
              </button>{' '}
              <button type="button" className="link epx-summary-link" onClick={() => goToStep('setup-summary')}>
                Setup summary
              </button>
            </p>
          </div>
          <ProfileStepper activeStep={activeStep} onSelect={goToStep} />
        </div>

        {isSummary ? (
          <SetupSummaryStep employeeId={employee.id} load={readinessLoad} onRetry={reloadReadiness} />
        ) : (
          <>
            {renderStep(activeStep, employee, (updated) => {
              setEmployee(updated);
              setTypedName(null);
              // A saved edit can change the server's readiness verdict — re-read it, never infer it here.
              reloadReadiness();
            }, reloadReadiness, (first, last) => setTypedName({ first, last, step: activeStep }), statusPill, footerInsidePanel ? footer : undefined)}

            {!footerInsidePanel && footer}
          </>
        )}
      </section>
    </div>
  );
}

function renderStep(
  step: Exclude<EmployeeProfileStepKey, 'setup-summary'>,
  employee: EmployeeResponse,
  onEmployeeUpdated: (updated: EmployeeResponse) => void,
  onPayRatesChanged: () => void,
  onNameTyped: (first: string, last: string) => void,
  statusPill: ReactNode,
  footer: ReactNode,
) {
  const StepComponent = STEP_COMPONENTS[step];
  return (
    <StepComponent
      employee={employee}
      onEmployeeUpdated={onEmployeeUpdated}
      onPayRatesChanged={onPayRatesChanged}
      onNameTyped={onNameTyped}
      statusPill={statusPill}
      footer={footer}
    />
  );
}

function chipValue(load: ReadinessLoad): ReadinessChipValue {
  switch (load.kind) {
    case 'loading': return 'loading';
    case 'loaded': return load.data.status;
    case 'forbidden': return 'forbidden';
    default: return 'unavailable';
  }
}

function chipAriaLabel(load: ReadinessLoad): string {
  switch (load.kind) {
    case 'loading': return 'Checking payroll readiness.';
    case 'loaded': return `Payroll readiness: ${load.data.status}.`;
    case 'forbidden': return 'You do not have permission to view payroll readiness.';
    default: return 'Payroll readiness is unavailable.';
  }
}

function statusModifier(status: number): 'active' | 'onleave' | 'terminated' {
  if (status === 1) return 'onleave';
  if (status === 2) return 'terminated';
  return 'active';
}
